import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { useAuth } from "../../Common/Configurations/AuthContext";

const initialFieldValues = {
  packageDateId: "00000000-0000-0000-0000-000000000000",
  startDate: "",
  endDate: "",
  companyId: "",
  userId: "",
  type: "",
};

const PackageDateModal = ({
  show,
  onHide,
  onPackageDateCreated,
  recordForEdit,
  type,
}) => {
  const { user } = useAuth();
  const [btnLoading, setBtnLoading] = useState(false);
  const [values, setValues] = useState(initialFieldValues);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (show) {
      if (recordForEdit) {
        setValues({
          ...recordForEdit,
          startDate: recordForEdit.startDate
            ? moment(recordForEdit.startDate).format("YYYY-MM-DD")
            : "",
          endDate: recordForEdit.endDate
            ? moment(recordForEdit.endDate).format("YYYY-MM-DD")
            : "",
        });
      } else {
        setValues({
          ...initialFieldValues,
          companyId: user?.cid,
          userId: user?.id,
          type: type,
        });
      }
    }
  }, [show, recordForEdit, user, type]);

  const validate = () => {
    let temp = {};
    temp.startDate = values.startDate.trim() !== "";
    temp.endDate = values.endDate.trim() !== "";
    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  const handleInputChange = (name, value) => {
    setValues({
      ...values,
      [name]: value,
    });
  };

  const resetAndHide = () => {
    setValues(initialFieldValues);
    setErrors({});
    onHide();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setBtnLoading(true);
      try {
        const isNew =
          values.packageDateId === "00000000-0000-0000-0000-000000000000";
        const payload = {
          ...values,
          startDate: moment(values.startDate, "DD-MM-YYYY").format(
            "YYYY-MM-DD"
          ),
          endDate: moment(values.endDate, "DD-MM-YYYY").format("YYYY-MM-DD"),
        };

        if (isNew) {
          await apiService.create(config.CREATEPACKAGEDATE, payload);
        } else {
          await apiService.update(config.UPDATEPACKAGEDATE, payload);
        }

        if (onPackageDateCreated) {
          onPackageDateCreated();
        }
        resetAndHide();
      } catch (error) {
        console.error("Failed to save package date:", error);
      } finally {
        setBtnLoading(false);
      }
    }
  };

  return (
    <Modal show={show} onHide={resetAndHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {values.packageDateId === "00000000-0000-0000-0000-000000000000"
            ? "Add"
            : "Edit"}{" "}
          Package Dates
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* --- The form JSX requires no changes --- */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="mb-3">
            <label className="form-label">Start Date</label>
            <Flatpickr
              className={`form-control ${
                errors.startDate === false ? "is-invalid" : ""
              }`}
              name="startDate"
              options={{ dateFormat: "d-m-Y" }}
              value={values.startDate}
              onChange={([date]) =>
                handleInputChange(
                  "startDate",
                  moment(date).format("DD-MM-YYYY")
                )
              }
              placeholder="Select Start Date"
            />

            {errors.startDate === false && (
              <div className="invalid-feedback">
                Please select a start date.
              </div>
            )}
          </div>

          <div className="mb-3">
            <label className="form-label">End Date</label>
            <Flatpickr
              className={`form-control ${
                errors.endDate === false ? "is-invalid" : ""
              }`}
              name="endDate"
              options={{
                dateFormat: "d-m-Y",
                minDate: values.startDate || null,
              }}
              value={values.endDate}
              onChange={([date]) =>
                handleInputChange("endDate", moment(date).format("DD-MM-YYYY"))
              }
              placeholder="Select End Date"
            />

            {errors.endDate === false && (
              <div className="invalid-feedback">Please select an end date.</div>
            )}
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <button
              type="button"
              className="btn btn-danger"
              onClick={resetAndHide}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={btnLoading}
            >
              {btnLoading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default PackageDateModal;
